/**
 * Create Folder Dialog
 *
 * Dialog for creating a new folder in the workspace.
 * Part of the Data Workspace OS feature set.
 *
 * Features:
 * - Folder name input with validation
 * - Parent folder selection for nesting
 */

import { useState } from 'react';
import { Folder, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@loader/ui';

import { useCreateFolderMutation } from '@/features/organization/hooks';
import { toast } from '@/lib/toast';

// ============================================================================
// TYPES
// ============================================================================

interface CreateFolderDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog should close */
  onClose: () => void;
  /** Parent folder ID for nested folders (optional) */
  parentFolderId?: string | null;
  /** Parent folder name for display (optional) */
  parentFolderName?: string | null;
  /** Callback when folder is created successfully */
  onCreated?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Dialog for creating a new folder
 */
export function CreateFolderDialog({
  isOpen,
  onClose,
  parentFolderId,
  parentFolderName,
  onCreated,
}: CreateFolderDialogProps) {
  const createFolderMutation = useCreateFolderMutation();

  const [name, setName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.folder.emptyName();
      return;
    }

    try {
      await createFolderMutation.mutateAsync({
        name: name.trim(),
        parent_folder_id: parentFolderId || null,
      });

      // Success has immediate visual feedback (folder appears in workspace)
      handleClose();
      onCreated?.();
    } catch (err) {
      // The API returns descriptive error messages like:
      // - "A folder named 'X' already exists in this location"
      // - "Parent folder not found"
      // Pass these through directly so users understand what went wrong
      const errorMessage = err instanceof Error ? err.message : undefined;
      toast.folder.createFailed(errorMessage);
    }
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="size-5 text-amber-500" />
              {parentFolderName ? 'Create Subfolder' : 'Create Folder'}
            </DialogTitle>
            <DialogDescription>
              {parentFolderName
                ? `Create a new folder inside "${parentFolderName}".`
                : 'Create a new folder to organize your tables and linked tables.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="folder-name">Name</Label>
              <Input
                id="folder-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter folder name"
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createFolderMutation.isPending || !name.trim()}>
              {createFolderMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Folder'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
